/**
 * SoleadLab 2.0 - Upload Data Modal
 *
 * Drag-and-drop JSON measurement file upload.
 * Pushes data to backend for evaluation + alarm checking.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import { useMonitoringStore } from '@/stores';
import './UploadSchemaModal.css'; // Reuse dropzone styles

interface UploadDataModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function UploadDataModal({ isOpen, onClose }: UploadDataModalProps) {
  const selectedSiteId = useMonitoringStore((s) => s.selectedSiteId);
  const pushUploadData = useMonitoringStore((s) => s.pushUploadData);

  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [jsonData, setJsonData] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    entitiesParsed: number;
    alarmsTriggered: number;
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Clear all state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setResult(null);
      setJsonData(null);
      setFileName(null);
      setIsDragging(false);
      setIsUploading(false);
    }
  }, [isOpen]);

  const processFile = useCallback(async (file: File) => {
    setError(null);
    setResult(null);
    setFileName(file.name);

    try {
      const text = await file.text();
      const json = JSON.parse(text);
      setJsonData(json);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse JSON file');
      setJsonData(null);
    }
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.json')) {
      processFile(file);
    } else {
      setError('Please drop a .json file');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handlePush = async () => {
    if (!jsonData || !selectedSiteId) return;

    setIsUploading(true);
    setError(null);
    try {
      await pushUploadData(jsonData);
      setResult({ entitiesParsed: 1, alarmsTriggered: 0 });
      // Close after brief delay so user sees success
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to push data');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Upload Measurement Data"
      size="md"
      footer={
        <div className="usm-footer">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handlePush}
            disabled={!jsonData || isUploading}
            loading={isUploading}
          >
            Push Data
          </Button>
        </div>
      }
    >
      <div className="usm-content">
        <p className="usm-description">
          Upload a JSON file with measurement data. Values will be evaluated against
          alarm rules and displayed on data points.
        </p>

        {/* Drop Zone */}
        <div
          className={`usm-dropzone ${isDragging ? 'usm-dropzone--active' : ''} ${jsonData ? 'usm-dropzone--done' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {jsonData ? (
            <div style={{ textAlign: 'center' }}>
              <span style={{ fontSize: 24 }}>&#10003;</span>
              <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>{fileName}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                Click to select a different file
              </div>
            </div>
          ) : (
            <>
              <svg className="usm-dropzone__icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span className="usm-dropzone__text">
                Drop measurement JSON here or <strong>click to browse</strong>
              </span>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        {error && (
          <div className="usm-error">
            <span className="usm-error__icon">!</span>
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div className="usm-preview">
            <div className="usm-preview__header">
              <span className="usm-preview__check">&#10003;</span>
              <span className="usm-preview__title">Data Pushed Successfully</span>
            </div>
            <div className="usm-preview__stats">
              {result.alarmsTriggered > 0
                ? `${result.alarmsTriggered} alarm(s) triggered`
                : 'No alarms triggered'}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
